import { create } from 'zustand';
import { createEmptyProject, normalizeProject, type Project, type PipeConnection } from '@/project/types';
import type { Device } from '@/devices/types';
import { APP_VERSION } from '@/app/appInfo';

/**
 * Project document + dirty flag. Only the metadata skeleton exists in Phase 1;
 * undo/redo (Immer patches, TD-022) arrives with the editors in Phase 4.
 *
 * An empty project name means "untitled" so the label follows the active language.
 */
export interface ProjectState {
  readonly project: Project;
  readonly isDirty: boolean;
  renameProject(name: string): void;
  newProject(): void;
  loadProject(project: any): void;
  clearDirty(): void;
  setProgram(program: Project['plc']['program']): void;
  setDevices(devices: Project['scene']['devices']): void;
  setPipes(pipes: readonly PipeConnection[]): void;
  addPipe(pipe: PipeConnection): void;
  updatePipe(pipeId: string, updates: Partial<PipeConnection>): void;
  removePipe(pipeId: string): void;
  setHmiProject(hmi: Project['hmi']): void;
  setWorkshopProject(workshop: Project['workshop']): void;
}

function syncPipesWithDevices(devices: readonly Device[], pipes: readonly PipeConnection[]): Device[] {
  if (!Array.isArray(devices)) return [];
  if (!Array.isArray(pipes)) return [...devices];

  return devices.map((dev) => {
    if (!dev || typeof dev !== 'object') return dev;
    const isFluidDev = dev.type === 'pump' || dev.type === 'valve' || (dev.type === 'motor' && dev.props?.['variant'] === 'pump_motor');
    if (!isFluidDev) return dev;

    const newProps = { ...(dev.props || {}) };
    let modified = false;

    for (const pipe of pipes) {
      if (!pipe || !pipe.from || !pipe.to) continue;
      const fromDev = devices.find((d) => d && d.id === pipe.from.deviceId);
      const toDev = devices.find((d) => d && d.id === pipe.to.deviceId);

      const fromIsOutlet = pipe.from.portType === 'outlet' || pipe.from.portId?.includes('outlet');
      const fromIsInlet = pipe.from.portType === 'inlet' || pipe.from.portId?.includes('inlet');
      const toIsOutlet = pipe.to.portType === 'outlet' || pipe.to.portId?.includes('outlet');
      const toIsInlet = pipe.to.portType === 'inlet' || pipe.to.portId?.includes('inlet');

      // Outlet connects to Tank inlet
      if (pipe.from.deviceId === dev.id && fromIsOutlet && toDev?.type === 'tank') {
        newProps['targetId'] = toDev.id;
        modified = true;
      } else if (pipe.to.deviceId === dev.id && toIsOutlet && fromDev?.type === 'tank') {
        newProps['targetId'] = fromDev.id;
        modified = true;
      }

      // Inlet connects to Tank outlet
      if (pipe.to.deviceId === dev.id && toIsInlet && fromDev?.type === 'tank') {
        newProps['sourceId'] = fromDev.id;
        modified = true;
      } else if (pipe.from.deviceId === dev.id && fromIsInlet && toDev?.type === 'tank') {
        newProps['sourceId'] = toDev.id;
        modified = true;
      }
    }

    if (newProps['sourceId'] && newProps['sourceId'] === newProps['targetId']) {
      newProps['sourceId'] = '';
      modified = true;
    }

    return modified ? { ...dev, props: newProps } : dev;
  });
}

const createProject = (): Project =>
  createEmptyProject({ name: '', nowIso: new Date().toISOString(), appVersion: APP_VERSION });

export const useProjectStore = create<ProjectState>()((set) => ({
  project: createProject(),
  isDirty: false,
  renameProject: (name) =>
    set((state) => ({
      project: {
        ...state.project,
        metadata: {
          ...state.project.metadata,
          name: name.trim(),
          modifiedAt: new Date().toISOString(),
        },
      },
      isDirty: true,
    })),
  newProject: () => set({ project: createProject(), isDirty: false }),
  loadProject: (rawProject) => {
    const normalized = normalizeProject(rawProject);
    const devices = syncPipesWithDevices(normalized.scene.devices, normalized.scene.pipes || []);
    set({
      project: {
        ...normalized,
        scene: {
          ...normalized.scene,
          devices,
        },
      },
      isDirty: false,
    });
  },
  clearDirty: () => set({ isDirty: false }),
  setProgram: (program) =>
    set((state) => ({
      project: {
        ...state.project,
        plc: { ...state.project.plc, program },
      },
      isDirty: true,
    })),
  setDevices: (devices) =>
    set((state) => ({
      project: {
        ...state.project,
        scene: { ...state.project.scene, devices },
      },
      isDirty: true,
    })),
  setPipes: (pipes) =>
    set((state) => ({
      project: {
        ...state.project,
        scene: {
          ...state.project.scene,
          devices: syncPipesWithDevices(state.project.scene.devices, pipes),
          pipes,
        },
      },
      isDirty: true,
    })),
  addPipe: (pipe) =>
    set((state) => {
      const currentPipes = state.project.scene.pipes || [];
      const updatedPipes = [...currentPipes.filter((p) => p.id !== pipe.id), pipe];
      const devices = syncPipesWithDevices(state.project.scene.devices, updatedPipes);

      return {
        project: {
          ...state.project,
          scene: {
            ...state.project.scene,
            devices,
            pipes: updatedPipes,
          },
        },
        isDirty: true,
      };
    }),
  updatePipe: (pipeId, updates) =>
    set((state) => {
      const currentPipes = state.project.scene.pipes || [];
      const updatedPipes = currentPipes.map((p) => (p.id === pipeId ? { ...p, ...updates } : p));
      return {
        project: {
          ...state.project,
          scene: {
            ...state.project.scene,
            pipes: updatedPipes,
          },
        },
        isDirty: true,
      };
    }),
  removePipe: (pipeId) =>
    set((state) => {
      const currentPipes = state.project.scene.pipes || [];
      const pipeToRemove = currentPipes.find((p) => p.id === pipeId);
      const updatedPipes = currentPipes.filter((p) => p.id !== pipeId);

      const cleanedDevices = state.project.scene.devices.map((dev) => {
        if (!pipeToRemove) return dev;
        const newProps = { ...dev.props };
        let modified = false;

        if (dev.id === pipeToRemove.from.deviceId || dev.id === pipeToRemove.to.deviceId) {
          const otherId = dev.id === pipeToRemove.from.deviceId ? pipeToRemove.to.deviceId : pipeToRemove.from.deviceId;
          if (newProps['sourceId'] === otherId) {
            newProps['sourceId'] = '';
            modified = true;
          }
          if (newProps['targetId'] === otherId) {
            newProps['targetId'] = '';
            modified = true;
          }
        }

        return modified ? { ...dev, props: newProps } : dev;
      });

      return {
        project: {
          ...state.project,
          scene: {
            ...state.project.scene,
            devices: syncPipesWithDevices(cleanedDevices, updatedPipes),
            pipes: updatedPipes,
          },
        },
        isDirty: true,
      };
    }),
  setHmiProject: (hmi) =>
    set((state) => ({
      project: {
        ...state.project,
        hmi,
      },
      isDirty: true,
    })),
  setWorkshopProject: (workshop) =>
    set((state) => ({
      project: {
        ...state.project,
        workshop,
      },
      isDirty: true,
    })),
}));
